import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "../utils/api"; // giữ nguyên util của bạn
import LoadingSpinner from "../components/LoadingSpinner";

const toLower = (v) => String(v ?? "").toLowerCase();
const isApproved = (s) => ["approved", "published"].includes(toLower(s));

export default function ServiceDetail() {
    const { id } = useParams();
    const sid = Number(id) || 0;

    const [service, setService] = useState(null);
    const [posts, setPosts] = useState([]);
    const [videoUrl, setVideoUrl] = useState("");
    const [loading, setLoading] = useState(true);
    const [errorText, setErrorText] = useState("");

    useEffect(() => {
        if (!sid) return;
        let alive = true;
        setLoading(true);
        setErrorText("");
        setService(null);
        setPosts([]);

        (async () => {
            // ---- 1) SERVICE INFO ----
            try {
                const r = await api(`/services/${sid}`);
                const svc = r?.data ?? r?.item ?? r ?? null;
                if (alive) setService(svc);
            } catch {
                try {
                    const r2 = await api(`/services`);
                    const list = Array.isArray(r2) ? r2 : (r2?.items || r2?.data || r2?.rows || []);
                    const svc = list.find((x) => Number(x?.id) === sid) || null;
                    if (alive) setService(svc);
                    if (!svc && alive) setErrorText("Không tìm thấy dịch vụ.");
                } catch {
                    if (alive) setErrorText("Không tải được thông tin dịch vụ.");
                }
            }

            // ---- 2) POSTS BY SERVICE ----
            try {
                const r = await api(`/services/${sid}/posts`);
                const arr = Array.isArray(r) ? r : (r?.items || r?.data || r?.rows || []);
                if (alive) setPosts(arr);
            } catch {
                try {
                    const r2 = await api(`/posts?service_id=${sid}&status=approved&limit=60`);
                    const arr2 = Array.isArray(r2) ? r2 : (r2?.items || r2?.data || r2?.rows || []);
                    const ok = (arr2 || []).filter((p) => isApproved(p?.status));
                    if (alive) setPosts(ok);
                } catch {
                    if (alive) setPosts([]);
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => { alive = false; };
    }, [sid]);

    if (loading) return <LoadingSpinner />;
    if (errorText) {
        return (
            <div className="max-w-6xl mx-auto p-4 sm:p-6">
                <p className="text-red-600">{errorText}</p>
            </div>
        );
    }
    if (!service) return null;

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6">
            {/* BỎ breadcrumb */}
            <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-red-600">
                {service?.name || `Dịch vụ #${sid}`}
            </h1>

            {/* BỎ "Tin đã duyệt (n)" */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {posts.map((p) => (
                    <PostCard
                        key={p.id || p.docId || `${p.service_id}-${p.title}`}
                        post={p}
                        onOpenVideo={setVideoUrl}
                    />
                ))}
            </div>

            {/* Modal Video */}
            {videoUrl ? (
                <div
                    className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                    onClick={() => setVideoUrl("")}
                >
                    <div
                        className="bg-black rounded-2xl w-full max-w-3xl aspect-video"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <iframe
                            title="video"
                            src={toYouTubeEmbed(videoUrl)}
                            className="w-full h-full rounded-2xl"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                        />
                    </div>
                </div>
            ) : null}

            {!posts.length && (
                <div className="text-gray-500 mt-4">Chưa có tin đã duyệt cho dịch vụ này.</div>
            )}
        </div>
    );
}

function InfoRow({ label, value }) {
    if (!value) return null;
    return (
        <div className="flex gap-2 text-sm">
            <span className="text-gray-500 shrink-0">{label}:</span>
            <span className="text-gray-800">{value}</span>
        </div>
    );
}

function PostCard({ post, onOpenVideo }) {
    const title = post?.title || "";
    const content = post?.content || post?.desc || "";
    const phone = post?.phone || "";
    const address = post?.address || "";
    const province = post?.province || post?.area_province || "";
    const district = post?.district || post?.area_district || "";
    const ward = post?.ward || post?.area_ward || "";
    const google_maps = post?.google_maps || "";
    const youtube = post?.youtube || "";

    const area = [ward, district, province].filter(Boolean).join(", ");

    const handleVideo = useCallback(() => {
        if (youtube && typeof onOpenVideo === "function") onOpenVideo(youtube);
    }, [youtube, onOpenVideo]);

    return (
        <article className="border rounded-2xl overflow-hidden bg-white shadow-sm">
            <div className="p-3 sm:p-4">
                <h3 className="text-base sm:text-lg font-semibold leading-snug">{title || "—"}</h3>

                {content ? (
                    <p className="text-sm text-gray-700 whitespace-pre-line mt-2 line-clamp-6">
                        {content}
                    </p>
                ) : null}

                <div className="mt-2 grid grid-cols-1 gap-1.5 text-sm">
                    <InfoRow label="📍 Địa chỉ" value={address} />
                    <InfoRow label="🗺️ Khu vực" value={area} />
                    <InfoRow label="📞 Điện thoại" value={phone} />
                </div>

                <div className="mt-3 flex gap-2">
                    {phone ? (
                        <a
                            href={`tel:${phone}`}
                            className="flex-1 text-center rounded-2xl bg-green-600 text-white py-2 font-semibold active:opacity-80"
                        >
                            Gọi
                        </a>
                    ) : null}

                    {google_maps ? (
                        <a
                            href={google_maps}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-center rounded-2xl bg-blue-600 text-white py-2 font-semibold active:opacity-80"
                        >
                            Maps
                        </a>
                    ) : null}

                    {youtube ? (
                        <button
                            type="button"
                            onClick={handleVideo}
                            className="flex-1 text-center rounded-2xl bg-red-600 text-white py-2 font-semibold active:opacity-80"
                        >
                            Video
                        </button>
                    ) : null}
                </div>
            </div>
        </article>
    );
}

function toYouTubeEmbed(url) {
    if (!url) return "";
    try {
        const u = new URL(url);
        const id = u.searchParams.get("v");
        if (id) return `https://www.youtube-nocookie.com/embed/${id}`;
        if (u.hostname.includes("youtu.be")) return `https://www.youtube-nocookie.com/embed${u.pathname}`;
        if (u.pathname.startsWith("/shorts/")) return `https://www.youtube-nocookie.com/embed/${u.pathname.split("/")[2]}`;
        return "";
    } catch {
        return "";
    }
}
